import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient, ApolloQueryResult, ApolloError } from "@apollo/client";
import {
    MolliePaymentStatus,
    MolliePaymentStatusFromEndUserAppDocument,
    MolliePaymentStatusFromEndUserAppQuery,
    MolliePaymentStatusFromEndUserAppQueryVariables,
    MolliePaymentStatusFromEndUserAppPublicDocument,
    MolliePaymentStatusFromEndUserAppPublicQuery,
    MolliePaymentStatusFromEndUserAppPublicQueryVariables,
} from "../../../generated/graphql";

export const POLLING_INTERVALS_MS = [1000, 2000, 3000, 5000, 8000, 13000, 21000] as const;
export const POLLING_TIMEOUT_MS = 60000;
export const TERMINAL_STATUSES = [
    "paid",
    "failed",
    "canceled",
    "expired",
    "chargebacked",
] as const;

export type TerminalStatus = (typeof TERMINAL_STATUSES)[number];

export function isTerminalStatus(status: string | null | undefined): status is TerminalStatus {
    if (!status) return false;
    return (TERMINAL_STATUSES as readonly string[]).includes(status);
}

export interface PollingOptions {
    scheduledPaymentId: string;
    banktransferSkip?: boolean;
    /**
     * Dispatch-Zahlungslink-Fall: Der Bucher klickt einen per E-Mail
     * versendeten Mollie-Link und ist NICHT in der EUA eingeloggt. Die
     * regulaere Query `molliePaymentStatusFromEndUserApp` ist `@Authorized`
     * und liefert dann UNAUTHENTICATED. Mit `usePublicEndpoint=true` fragt der
     * Hook stattdessen die auth-freie `molliePaymentStatusFromEndUserAppPublic`
     * ab. Die Public-Variante liefert inzwischen die Ueberweisungsdaten
     * (bankTransferDetails/expiresAt) und Ausweg-Links (statusUrl/payOnlineUrl),
     * aber KEINE bookingId / availableMollieMethods — diese sind in
     * `MolliePaymentStatus` ohnehin optional und werden hier als
     * `null`/`undefined` belassen.
     */
    usePublicEndpoint?: boolean;
}

export interface PollingResult {
    data: MolliePaymentStatus | null;
    loading: boolean;
    error: Error | null;
    isTimeout: boolean;
    /**
     * Zugriff verweigert: Ein FREMDER Bucher (nicht Eigentuemer der Zahlung) ist
     * eingeloggt. Der `@Authorized`-Endpoint wirft dann `ForbiddenError`
     * (extensions.code === "FORBIDDEN" bzw. HTTP 403). Anders als transiente
     * Fehler ist das permanent — das Polling wird hart gestoppt und die Seite
     * zeigt eine Kein-Zugriff-Card statt endlos "in Bearbeitung".
     */
    accessDenied: boolean;
    manualRefetch: () => Promise<void>;
}

export function useMolliePaymentStatusPolling(opts: PollingOptions): PollingResult {
    const { scheduledPaymentId, banktransferSkip, usePublicEndpoint } = opts;
    const client = useApolloClient();

    const [data, setData] = useState<MolliePaymentStatus | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [isTimeout, setIsTimeout] = useState<boolean>(false);
    const [accessDenied, setAccessDenied] = useState<boolean>(false);

    // Refs fuer Cleanup-Safety (React-18-StrictMode-Guard)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const intervalIndexRef = useRef<number>(0);
    const totalElapsedRef = useRef<number>(0);
    const unmountedRef = useRef<boolean>(false);
    const stoppedRef = useRef<boolean>(false);

    const runQuery = useCallback(async (): Promise<MolliePaymentStatus | null> => {
        try {
            let status: MolliePaymentStatus | null;
            if (usePublicEndpoint) {
                // Auth-freier Pfad fuer ausgeloggte Bucher (Dispatch-Zahlungslink).
                const result: ApolloQueryResult<MolliePaymentStatusFromEndUserAppPublicQuery> =
                    await client.query<
                        MolliePaymentStatusFromEndUserAppPublicQuery,
                        MolliePaymentStatusFromEndUserAppPublicQueryVariables
                    >({
                        query: MolliePaymentStatusFromEndUserAppPublicDocument,
                        variables: { scheduledPaymentId },
                        fetchPolicy: "network-only",
                    });
                if (unmountedRef.current) return null;
                const publicStatus =
                    result.data?.molliePaymentStatusFromEndUserAppPublic ?? null;
                // Public-Typ in die gemeinsame MolliePaymentStatus-Form heben.
                // Die Public-Query liefert inzwischen ebenfalls die
                // Ueberweisungsdaten (bankTransferDetails/expiresAt) und die
                // Ausweg-Links (statusUrl/payOnlineUrl) — diese reichen wir durch,
                // damit auch ausgeloggte Bucher ihre Bankdaten + Links sehen.
                // Nur die auth-pflichtigen Felder (bookingId / availableMollieMethods)
                // bleiben optional undefined, sodass die Retry-/Method-Picker-UI
                // fuer ausgeloggte Bucher korrekterweise nicht rendert.
                status = publicStatus
                    ? {
                          molliePaymentId: publicStatus.molliePaymentId,
                          status: publicStatus.status,
                          paidAt: publicStatus.paidAt,
                          expiresAt: publicStatus.expiresAt,
                          amount: publicStatus.amount,
                          amountDetails: publicStatus.amountDetails,
                          bankTransferDetails: publicStatus.bankTransferDetails,
                          statusUrl: publicStatus.statusUrl,
                          payOnlineUrl: publicStatus.payOnlineUrl,
                          checkoutUrl: publicStatus.checkoutUrl,
                          failureReason: publicStatus.failureReason,
                          courseTitle: publicStatus.courseTitle,
                          courseStartDate: publicStatus.courseStartDate,
                      }
                    : null;
            } else {
                const result: ApolloQueryResult<MolliePaymentStatusFromEndUserAppQuery> =
                    await client.query<
                        MolliePaymentStatusFromEndUserAppQuery,
                        MolliePaymentStatusFromEndUserAppQueryVariables
                    >({
                        query: MolliePaymentStatusFromEndUserAppDocument,
                        variables: { scheduledPaymentId },
                        fetchPolicy: "network-only",
                    });
                if (unmountedRef.current) return null;
                status = result.data?.molliePaymentStatusFromEndUserApp ?? null;
            }
            setData(status);
            setError(null);
            return status;
        } catch (e) {
            if (unmountedRef.current) return null;
            // Zugriff verweigert (fremder Bucher): Der `@Authorized`-Endpoint wirft
            // ForbiddenError (extensions.code === "FORBIDDEN") bzw. HTTP 403. Anders
            // als transiente Fehler ist das permanent → Polling hart stoppen
            // (stoppedRef wird von scheduleNext/Timeout/Visibility-Listener geprueft)
            // und accessDenied setzen, statt weiterzupollen.
            const apolloErr = e as ApolloError;
            const isForbidden =
                apolloErr?.graphQLErrors?.[0]?.extensions?.code === "FORBIDDEN" ||
                (apolloErr?.networkError as { statusCode?: number } | null)?.statusCode === 403;
            if (isForbidden) {
                setAccessDenied(true);
                stoppedRef.current = true;
                return null;
            }
            // D-26: Transient-Error-Toleranz, kein stopPolling
            // eslint-disable-next-line no-console
            console.warn("[useMolliePaymentStatusPolling] query error, will retry", e);
            setError(e instanceof Error ? e : new Error(String(e)));
            return null;
        } finally {
            if (!unmountedRef.current) setLoading(false);
        }
    }, [client, scheduledPaymentId, usePublicEndpoint]);

    const scheduleNext = useCallback(() => {
        if (stoppedRef.current || unmountedRef.current) return;

        if (totalElapsedRef.current >= POLLING_TIMEOUT_MS) {
            setIsTimeout(true);
            stoppedRef.current = true;
            return;
        }
        if (intervalIndexRef.current >= POLLING_INTERVALS_MS.length) {
            setIsTimeout(true);
            stoppedRef.current = true;
            return;
        }

        const wait = POLLING_INTERVALS_MS[intervalIndexRef.current];
        timerRef.current = setTimeout(async () => {
            if (stoppedRef.current || unmountedRef.current) return;
            if (document.visibilityState === "hidden") {
                // Will resume via visibilitychange listener below
                return;
            }
            totalElapsedRef.current += wait;
            intervalIndexRef.current += 1;
            const status = await runQuery();
            if (stoppedRef.current || unmountedRef.current) return;
            if (status && isTerminalStatus(status.status)) {
                stoppedRef.current = true;
                return;
            }
            scheduleNext();
        }, wait);
    }, [runQuery]);

    const stopPolling = useCallback(() => {
        stoppedRef.current = true;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const manualRefetch = useCallback(async () => {
        setLoading(true);
        const status = await runQuery();
        if (status && isTerminalStatus(status.status)) {
            stopPolling();
        }
    }, [runQuery, stopPolling]);

    // Page-Visibility-Listener
    useEffect(() => {
        function handleVisibility() {
            if (document.visibilityState === "visible") {
                if (stoppedRef.current || unmountedRef.current) return;
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                // Sofortige 1x-Query + Resume
                void (async () => {
                    const status = await runQuery();
                    if (status && isTerminalStatus(status.status)) {
                        stopPolling();
                        return;
                    }
                    scheduleNext();
                })();
            } else if (document.visibilityState === "hidden") {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
            }
        }
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, [runQuery, scheduleNext, stopPolling]);

    // Main lifecycle
    useEffect(() => {
        unmountedRef.current = false;
        stoppedRef.current = false;
        intervalIndexRef.current = 0;
        totalElapsedRef.current = 0;
        setAccessDenied(false);

        if (banktransferSkip) {
            // D-13: One-Shot-Query beim Mount statt Polling
            void runQuery();
            stoppedRef.current = true;
        } else {
            // Initial sofort-Query, dann Backoff
            void (async () => {
                const status = await runQuery();
                if (status && isTerminalStatus(status.status)) {
                    stopPolling();
                    return;
                }
                scheduleNext();
            })();
        }

        return () => {
            unmountedRef.current = true;
            stoppedRef.current = true;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduledPaymentId, banktransferSkip, usePublicEndpoint]);

    return { data, loading, error, isTimeout, accessDenied, manualRefetch };
}
